import { ChevronLeft, Loader2 } from "lucide-react";
import React from "react";

import { DeactivatedHistoryProps } from "./DeactivatedHistoryTable";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { RehireRecommendation } from "@/constants/employees/employee.constants";
import useQueryID from "@/hooks/useQueryId";
import { ItemProps } from "@/interfaces/employee.interface";
import { DisplayCard } from "@/pages/waste-management/weight-scale/components/preview-ticket";
import { useGetSingleEmployeeQuery } from "@/redux/api/employeeQuery";
import { TGetSingleEmployee } from "@/schemas/employee-schema";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";

type TProjectSheet = {
  isOpen: boolean;
  toggleOpen: () => void;
};

type TFormattedDetails = {
  employeeDetails: {
    name: string;
    address: string;
    email: string;
    phoneNumber: string;
    employeeType: string;
  };
  deactivatedHistory: {
    deactivatedDate: string;
    deactivatorName: string;
    deactivatorEmail: string;
    deactivatorPhoneNumber: string;
  }[];
  terminationHistory: {
    noticeDate: string;
    terminationDate: string;
    terminationReason: string;
    severancePay: string;
    attendance: string;
    initative: string;
    cooperation: string;
    workQuality: string;
    jobKnowledge: string;
    rehireRecommendation: string;
    terninatorName: string;
    terminatedPhone: string;
    additionalComment: string;
  }[];
};

const useFormattedDetails = (
  data: TGetSingleEmployee,
): TFormattedDetails | undefined => {
  const [formattedDetails, setFormattedDetails] =
    React.useState<TFormattedDetails>();

  React.useEffect(() => {
    if (!data) {
      return;
    }
    const singleEmployee = data;
    const employeeDetails = {
      name: `${singleEmployee?.user?.firstName} ${singleEmployee?.user?.middleName ?? ""} ${singleEmployee?.user?.lastName}`,
      address: singleEmployee?.user?.address as string,
      email: singleEmployee?.user?.email ?? "",
      phoneNumber: singleEmployee?.user?.phoneNumber as string,
      employeeType: singleEmployee?.user?.employmentType as string,
    };
    const deactivatedHistory = singleEmployee?.employeeStatusRecords.filter(
      (item) => item?.action === "DEACTIVATED",
    ) as DeactivatedHistoryProps[];

    const deactivatedHistoryData = deactivatedHistory.map((item) => ({
      deactivatedDate: item?.createdAt,
      deactivatorName: item?.actor?.firstName,
      deactivatorEmail: item?.actor?.email,
      deactivatorPhoneNumber: item?.actor?.phoneNumber,
    }));

    const terminationHistory = singleEmployee?.employeeStatusRecords.filter(
      (item) => item?.action === "TERMINATED",
    ) as ItemProps[];

    const terminationHistoryData = terminationHistory.map((item) => ({
      noticeDate: item?.actionDetails?.employeeNotice?.date as string,
      terminationDate: item?.actionDetails.terminationDate as string,
      terminationReason: item?.actionDetails?.terminationReason,
      severancePay: item?.actionDetails?.severancePay,
      attendance: item?.actionDetails?.employeeEvaluation?.attendance,
      initative: item?.actionDetails?.employeeEvaluation?.initiative,
      cooperation: item?.actionDetails?.employeeEvaluation?.cooperation,
      workQuality: item?.actionDetails?.employeeEvaluation?.workQuality,
      jobKnowledge: item?.actionDetails?.employeeEvaluation?.jobKnowledge,
      rehireRecommendation: item?.actionDetails?.rehireRecommendation,
      terninatorName: item?.actor?.firstName,
      terminatedPhone: item?.actor?.phoneNumber,
      additionalComment: item?.actionDetails?.additionalComment,
    }));
    setFormattedDetails({
      employeeDetails,
      deactivatedHistory: deactivatedHistoryData,
      terminationHistory: terminationHistoryData,
    });
  }, [data]);

  return formattedDetails;
};

const getBadgeVariant = (
  status: string,
): "success" | "lightYellow" | "destructive" => {
  switch (status) {
    case RehireRecommendation.Without_Reservation:
      return "success";
    case RehireRecommendation.With_Reservation:
      return "lightYellow";
    default:
      return "destructive";
  }
};

const renderDisplayCards = (details: TFormattedDetails): JSX.Element => {
  const renderCards = (
    data: Record<string, string>,
    title: string,
  ): JSX.Element => (
    <>
      <h2 className='mt-4 text-xl font-medium'>{title}</h2>
      {Object.entries(data).map(([key, value]) => (
        <DisplayCard
          key={key}
          dataHeader={key}
          data={
            key === "deactivatedDate" ||
            key === "noticeDate" ||
            key === "terminationDate"
              ? dateFormat(value, "dd/MM/yyyy")
              : value || "N/A"
          }
          showBadge={{
            header: ["rehireRecommendation"],
            badgeVariant: getBadgeVariant,
          }}
        />
      ))}
    </>
  );

  return (
    <>
      {renderCards(details?.employeeDetails, "Employee Details")}
      {details?.deactivatedHistory?.length === 1
        ? renderCards(details?.deactivatedHistory[0], "Deactivated History")
        : details?.deactivatedHistory?.map((item, index) => (
            <div key={index}>
              {renderCards(item, `Deactivated History ${index + 1}`)}
            </div>
          ))}
      {details?.terminationHistory?.length === 1
        ? renderCards(details?.terminationHistory[0], "Termination History")
        : details?.terminationHistory?.map((item, index) => (
            <div key={index}>
              {renderCards(item, `Termination History ${index + 1}`)}
            </div>
          ))}
    </>
  );
};

const TerminationSheet = ({
  isOpen,
  toggleOpen,
}: TProjectSheet): React.JSX.Element => {
  const { id, reset } = useQueryID("rowId");
  const { data } = useGetSingleEmployeeQuery({
    employeeId: id,
  });

  const handleExit = (): void => {
    localStorage.removeItem("leadsSheetName");
    reset();
    toggleOpen();
  };
  const formattedDetails = useFormattedDetails(data as TGetSingleEmployee);

  return (
    <Sheet open={isOpen} onOpenChange={handleExit}>
      <SheetContent className='min-w-[850px] overflow-y-scroll'>
        <SheetHeader>
          <div className='flex items-center gap-2 cursor-pointer'>
            <div
              className='p-2 border rounded-md border-primary'
              onClick={handleExit}
            >
              <ChevronLeft size={20} color='blue' />
            </div>

            <SheetTitle className='text-xl font-normal'>
              {localStorage.getItem("leadsSheetName")}
            </SheetTitle>
          </div>
        </SheetHeader>

        <div className='flex flex-col gap-4'>
          {formattedDetails ? (
            renderDisplayCards(formattedDetails)
          ) : (
            <Loader2 className='w-12 h-12 animate-spin' />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TerminationSheet;
